/**
 * Meta Pixel Utilities for Facebook Conversion API (CAPI)
 *
 * This module handles Meta Pixel's _fbc and _fbp cookies and constructs
 * the required attribution parameters for Segment identity and tracking.
 *
 * References:
 * - https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/
 * - https://segment.com/docs/unify/traits/custom-traits/
 */

/**
 * Meta Pixel cookie names
 */
export const META_PIXEL_COOKIES = {
    FBC: '_fbc',
    FBP: '_fbp',
}

/**
 * Gets a cookie value by name
 */
function getCookie(name: string): string | null {
    if (typeof document === 'undefined') {
        return null
    }

    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)

    if (parts.length === 2) {
        return parts.pop()?.split(';').shift() || null
    }

    return null
}

/**
 * Sets a cookie with the given name, value, and expiration days
 */
function setCookie(name: string, value: string, days = 90): void {
    if (typeof document === 'undefined') {
        return
    }

    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    const expires = `expires=${date.toUTCString()}`
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`
}

/**
 * Reads the _fbp cookie set by Meta Pixel
 * Format: fb.{subdomain_index}.{creation_time}.{random_number}
 *
 * @returns The _fbp cookie value, or null if not found
 */
export function getFbpFromCookie(): string | null {
    return getCookie(META_PIXEL_COOKIES.FBP)
}

/**
 * Reads the _fbc cookie set by Meta Pixel
 * Format: fb.{subdomain_index}.{creation_time}.{fbclid}
 *
 * @returns The _fbc cookie value, or null if not found
 */
export function getFbcFromCookie(): string | null {
    return getCookie(META_PIXEL_COOKIES.FBC)
}

/**
 * Constructs the _fbc value from fbclid parameter
 * Format: fb.1.{unix_timestamp}.{fbclid}
 *
 * @param fbclid - The Facebook Click ID from the URL parameter
 * @returns The constructed _fbc value
 */
export function constructFbc(fbclid: string): string {
    const unixTimestamp = Math.floor(Date.now() / 1000)
    return `fb.1.${unixTimestamp}.${fbclid}`
}

/**
 * Constructs the _fbp value (browser ID) if not present
 * Format: fb.1.{unix_timestamp}.{random_number}
 *
 * @returns The constructed _fbp value
 */
export function constructFbp(): string {
    const unixTimestamp = Math.floor(Date.now() / 1000)
    const randomNumber = Math.floor(Math.random() * 2147483647) // Max 32-bit integer
    return `fb.1.${unixTimestamp}.${randomNumber}`
}

/**
 * Gets or creates the _fbc value
 * Priority:
 * 1. Read from existing _fbc cookie
 * 2. Construct from fbclid if available
 * 3. Return null if neither is available
 *
 * @param fbclid - Optional Facebook Click ID from URL
 * @returns The _fbc value or null
 */
export function getOrCreateFbc(fbclid?: string | null): string | null {
    // First, try to read from cookie
    const existingFbc = getFbcFromCookie()
    if (existingFbc) {
        return existingFbc
    }

    // If fbclid is provided, construct and store _fbc
    if (fbclid) {
        const fbc = constructFbc(fbclid)
        setCookie(META_PIXEL_COOKIES.FBC, fbc, 90)
        return fbc
    }

    return null
}

/**
 * Gets or creates the _fbp value
 * Priority:
 * 1. Read from existing _fbp cookie
 * 2. Construct a new value
 *
 * @returns The _fbp value
 */
export function getOrCreateFbp(): string {
    // First, try to read from cookie
    const existingFbp = getFbpFromCookie()
    if (existingFbp) {
        return existingFbp
    }

    // Construct and store new _fbp
    const fbp = constructFbp()
    setCookie(META_PIXEL_COOKIES.FBP, fbp, 90)
    return fbp
}

/**
 * Extracts fbclid from URL query parameters
 *
 * @param url - The URL to extract fbclid from (defaults to current URL)
 * @returns The fbclid value or null
 */
export function getFbclidFromUrl(url?: string): string | null {
    if (typeof window === 'undefined') {
        return null
    }

    const targetUrl = url || window.location.href
    const urlParams = new URLSearchParams(new URL(targetUrl).search)
    return urlParams.get('fbclid')
}

/**
 * Gets all Meta Pixel attribution data for Segment traits
 * This should be called when identifying a user or tracking events
 *
 * @param fbclid - Optional Facebook Click ID from URL (if not provided, will try to extract from current URL)
 * @returns An object with fbc, fbp, and fbclid
 */
export function getMetaPixelTraits(fbclid?: string | null): {
    fbc: string | null
    fbp: string
    fbclid: string | null
} {
    // Try to get fbclid from parameter or URL
    const clickId = fbclid ?? getFbclidFromUrl()

    // Get or create fbc (will use cookie or construct from fbclid)
    const fbc = getOrCreateFbc(clickId)

    // Get or create fbp (will use cookie or construct new)
    const fbp = getOrCreateFbp()

    return {
        fbc: fbc,
        fbp: fbp,
        fbclid: clickId,
    }
}
